/* eslint-disable @typescript-eslint/no-explicit-any */
import type { QuickTerreiroSignupPayload } from "../types/terreiroSignup.types";
import type { QuickTerreiroSocialSignupPayload } from "../types/terreiroSignup.types";

/**
 * Sanitizer for quick terreiro signup payload.
 * Only includes essential fields required for quick signup.
 *
 * Essential fields:
 * - firstName, lastName, email, phone (personal info)
 * - terreiroName, religionId (terreiro info)
 * - password, confirmPassword (security)
 */
export function sanitizeQuickTerreiroSignupPayload(
  payload: Partial<QuickTerreiroSignupPayload>,
): QuickTerreiroSignupPayload {
  const sanitized: any = {};

  // Essential fields - REQUIRED
  const essentialFields = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "terreiroName",
    "religionId",
    "password",
    "confirmPassword",
  ];

  // Copy essential fields, trimming string values
  for (const field of essentialFields) {
    const value = payload[field as keyof QuickTerreiroSignupPayload];
    if (typeof value === "string") {
      sanitized[field] = value.trim();
    } else {
      sanitized[field] = value ?? "";
    }
  }

  // Normalize phone number - remove non-numeric chars except + for country code
  if (sanitized.phone) {
    // Keep only digits and + for country code
    sanitized.phone = sanitized.phone.replace(/[^\d+]/g, "");
  }

  // Normalize email to lowercase
  if (sanitized.email) {
    sanitized.email = sanitized.email.toLowerCase().trim();
  }

  if (payload.code && payload.code.trim() !== "") {
    sanitized.code = payload.code.trim();
  }

  if (payload.signupCode && payload.signupCode.trim() !== "") {
    sanitized.signupCode = payload.signupCode.trim();
  }

  return sanitized as QuickTerreiroSignupPayload;
}

/**
 * Sanitizer for quick terreiro social signup payload.
 * Only includes essential social quick fields and omits password fields.
 */
export function sanitizeQuickTerreiroSocialSignupPayload(
  payload: Partial<QuickTerreiroSocialSignupPayload>,
): QuickTerreiroSocialSignupPayload {
  const sanitized: any = {};

  const essentialFields = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "terreiroName",
    "religionId",
  ];

  for (const field of essentialFields) {
    const value = payload[field as keyof QuickTerreiroSocialSignupPayload];
    if (typeof value === "string") {
      sanitized[field] = value.trim();
    } else {
      sanitized[field] = value ?? "";
    }
  }

  if (sanitized.phone) {
    sanitized.phone = sanitized.phone.replace(/[^\d+]/g, "");
  }

  if (sanitized.email) {
    sanitized.email = sanitized.email.toLowerCase().trim();
  }

  if (payload.code && payload.code.trim() !== "") {
    sanitized.code = payload.code.trim();
  }

  if (payload.signupCode && payload.signupCode.trim() !== "") {
    sanitized.signupCode = payload.signupCode.trim();
  }

  return sanitized as QuickTerreiroSocialSignupPayload;
}
